// Shared
import Configs from '@/Configs';
import { fetchOptions, handleResponse } from '@/common/apiCalls';

export type SelfOnboardingRegisterPayload = {
  companyName: string;
  country: string;
  firstName: string;
  lastName: string;
  email: string;
  password?: string;
  /** Work-account signup ticket. When set, password is omitted and verification email is skipped. */
  signupTicket?: string;
  /** Campaign locale (`en`, `es-ES`, …) or legacy `EN`/`ES`. */
  language?: string;
  /** Optional `?campaign=` slug from the public signup URL. Omit when absent so the backend default applies. */
  campaign?: string;
};

export type SelfOnboardingVerifyResult = {
  brandingTicket?: string;
  status?: 'already_active' | 'ok';
  language?: string | null;
  founderName?: string;
  companyName?: string;
};

const SELF_ONBOARDING_CAMPAIGN_LANGUAGES = [
  'en',
  'en-GB',
  'es',
  'es-ES',
  'pt-PT',
  'pt-BR',
  'fr',
  'ca',
  'de',
] as const;

export type SelfOnboardingCampaignLanguage = (typeof SELF_ONBOARDING_CAMPAIGN_LANGUAGES)[number];

/**
 * True when the value is a locale the public signup form can render.
 *
 * @param value Campaign language from the API
 */
export function isSelfOnboardingCampaignLanguage(
  value: unknown
): value is SelfOnboardingCampaignLanguage {
  return (
    typeof value === 'string' &&
    (SELF_ONBOARDING_CAMPAIGN_LANGUAGES as readonly string[]).includes(value)
  );
}

/**
 * Public signup locale for a campaign slug. Missing or unknown campaigns return null.
 *
 * @param campaign Campaign slug from `?campaign=`
 */
export async function getSelfOnboardingCampaignLanguage(
  campaign: string,
  correlationId: string | null = null
): Promise<string | null> {
  const response = await fetch(
    `${Configs.platformGetSelfOnboardingCampaignLanguageAPI}?campaign=${encodeURIComponent(campaign)}`,
    fetchOptions('GET', null, null, 'json', correlationId)
  );
  const result = await handleResponse(response);
  if (!result || typeof result !== 'object' || typeof result.language !== 'string') {
    return null;
  }
  return isSelfOnboardingCampaignLanguage(result.language) ? result.language : null;
}

const verifyInFlight = new Map<string, Promise<SelfOnboardingVerifyResult>>();

export type SelfOnboardingBrandingPayload = {
  brandingTicket: string;
  skip?: boolean;
  colorHex?: string;
  logoUrl?: string;
};

/** Activation + session issued like login so the founder skips the login screen. */
export type SelfOnboardingBrandingResult = {
  status: 'ok';
  companyID: number;
  employeeID: number;
  bearerToken: string;
  csrfToken: string;
  profile?: Record<string, unknown>;
};

export type CompanyValuesSetupValue = {
  value: string;
  description: string;
};

export type CompanyValuesSetupPayload =
  | { action: 'skip' }
  | {
      action: 'complete';
      values: CompanyValuesSetupValue[];
    };

export type CompanyValuesSetupResult = {
  valuesSetupStatus: 'pending' | 'skipped' | 'completed';
  companyValues?: CompanyValuesSetupValue[];
  valuesSetupSkippedAt?: string | null;
  valuesSetupCompletedAt?: string | null;
};

export type SelfOnboardingSsoStartResult = {
  signupTicket: string;
  email: string;
  firstName: string;
  lastName: string;
  provider: 'google' | 'microsoft';
};

/**
 * Exchange a Google access token or Microsoft id token for a signup ticket.
 * 400 `WORK_ACCOUNT_REQUIRED` and 409 codes are returned as `{ code }` so the form can explain them.
 *
 * @param body Provider, token, and campaign slug
 * @param correlationId Request correlation id
 */
export async function postSelfOnboardingSsoStart(
  body: { provider: 'google' | 'microsoft'; token: string; campaign: string },
  correlationId: string | null = null
): Promise<SelfOnboardingSsoStartResult | { code: string } | number> {
  const response = await fetch(
    Configs.platformPostSelfOnboardingSsoStartAPI,
    fetchOptions('POST', body, null, 'json', correlationId)
  );
  let parsed: { code?: string; signupTicket?: string } | null = null;
  try {
    parsed = await response.json();
  } catch {
    parsed = null;
  }
  if (response.status === 200 && parsed && typeof parsed.signupTicket === 'string') {
    return parsed as SelfOnboardingSsoStartResult;
  }
  if ((response.status === 400 || response.status === 409) && parsed && typeof parsed.code === 'string') {
    return { code: parsed.code };
  }
  return response.status;
}

/**
 * Public founder signup. HTTP 200 is `{ status: 'ok' }` for email and password.
 * A work-account ticket returns `{ brandingTicket }`. HTTP 409 means the email is already a login.
 */
export async function postSelfOnboardingRegister(
  body: SelfOnboardingRegisterPayload,
  correlationId: string | null = null
) {
  const response = await fetch(
    Configs.platformPostSelfOnboardingRegisterAPI,
    fetchOptions('POST', body, null, 'json', correlationId)
  );
  return handleResponse(response);
}

/**
 * Resend verification email for a pending founder mailbox.
 */
export async function postSelfOnboardingResend(
  body: { email: string; language?: string },
  correlationId: string | null = null
) {
  const response = await fetch(
    Configs.platformPostSelfOnboardingResendAPI,
    fetchOptions('POST', body, null, 'json', correlationId)
  );
  return handleResponse(response);
}

/**
 * Consume the email token; returns `{ brandingTicket }` or `{ status: 'already_active' }`.
 * In-flight and completed successes are cached per token so React StrictMode remounts
 * do not POST twice.
 */
export async function postSelfOnboardingVerify(
  token: string,
  correlationId: string | null = null
): Promise<SelfOnboardingVerifyResult> {
  const cached = verifyInFlight.get(token);
  if (cached) {
    return cached;
  }

  const pending = (async () => {
    const response = await fetch(
      Configs.platformPostSelfOnboardingVerifyAPI,
      fetchOptions('POST', { token }, null, 'json', correlationId)
    );
    return (await handleResponse(response)) as SelfOnboardingVerifyResult;
  })();

  verifyInFlight.set(token, pending);
  try {
    const body = await pending;
    const isSuccess =
      typeof body === 'object' &&
      body !== null &&
      (typeof body.brandingTicket === 'string' || body.status === 'already_active');
    if (!isSuccess) {
      verifyInFlight.delete(token);
    }
    return body;
  } catch (error) {
    verifyInFlight.delete(token);
    throw error;
  }
}

/**
 * Skip or apply branding, activate the tenant, and return a login session.
 */
export async function postSelfOnboardingBranding(
  body: SelfOnboardingBrandingPayload,
  correlationId: string | null = null
): Promise<SelfOnboardingBrandingResult> {
  const response = await fetch(
    Configs.platformPostSelfOnboardingBrandingAPI,
    fetchOptions('POST', body, null, 'json', correlationId)
  );
  return (await handleResponse(response)) as SelfOnboardingBrandingResult;
}

/** Skip or complete the authenticated founder's guided values setup. */
export async function postSelfOnboardingValuesSetup(
  companyID: string | number,
  employeeID: string | number,
  token: string,
  body: CompanyValuesSetupPayload,
  correlationId: string | null = null
): Promise<CompanyValuesSetupResult> {
  const query = new URLSearchParams({
    companyID: String(companyID),
    employeeID: String(employeeID),
  });
  const response = await fetch(
    `${Configs.platformPostSelfOnboardingValuesSetupAPI}?${query.toString()}`,
    fetchOptions('POST', body, token, 'json', correlationId)
  );
  return (await handleResponse(response)) as CompanyValuesSetupResult;
}

export type GettingStartedStepDto = {
  id: string;
  title: string;
  description: string | null;
  completed: boolean;
  completedAt: string | null;
  action?: string | null;
};

export type GettingStartedCompleteResult = {
  status: 'pending' | 'completed';
  campaignSlug: string;
  steps: GettingStartedStepDto[];
};

/** Mark one snapshotted getting-started step complete (founder-only). */
export async function postSelfOnboardingGettingStartedComplete(
  companyID: string | number,
  employeeID: string | number,
  token: string,
  body: { stepId: string },
  correlationId: string | null = null
): Promise<GettingStartedCompleteResult> {
  const query = new URLSearchParams({
    companyID: String(companyID),
    employeeID: String(employeeID),
  });
  const response = await fetch(
    `${Configs.platformPostSelfOnboardingGettingStartedCompleteAPI}?${query.toString()}`,
    fetchOptions('POST', body, token, 'json', correlationId)
  );
  return (await handleResponse(response)) as GettingStartedCompleteResult;
}

/** Ensure agreement+project, assign teammate, complete assign step (founder-only). */
export async function postSelfOnboardingGettingStartedAssignAgreementProject(
  companyID: string | number,
  employeeID: string | number,
  token: string,
  body: {
    stepId: string;
    targetEmployeeID?: number | null;
    agreementID?: number | null;
    templateKey?: string | null;
    projectID?: number | null;
    newProjectName?: string | null;
    assignments?: Array<{ targetEmployeeID: number; templateKey: string }> | null;
  },
  correlationId: string | null = null
): Promise<GettingStartedCompleteResult> {
  const query = new URLSearchParams({
    companyID: String(companyID),
    employeeID: String(employeeID),
  });
  const response = await fetch(
    `${Configs.platformPostSelfOnboardingGettingStartedAssignAgreementProjectAPI}?${query.toString()}`,
    fetchOptions('POST', body, token, 'json', correlationId)
  );
  return (await handleResponse(response)) as GettingStartedCompleteResult;
}
